/**
 * In-game menu overlay — v0M15.5.
 *
 * Opened by the Escape key while the sim is running. Presents six
 * actions:
 *
 *   Resume              → close menu, restore prior sim speed.
 *   Save Game           → mount slot-picker in save mode above the menu.
 *   Load Game           → mount slot-picker in load mode above the menu.
 *   Settings            → mount settings modal above the menu.
 *   Return to Main Menu → confirm dialog → reload page.
 *   Exit                → same tear-down as main-menu Exit (MFix2.1).
 *
 * Design lock (v0M15-design):
 *   - The menu is layered above HUDs / panels / battle view. When it
 *     opens, the sim auto-pauses; when it closes via Resume, the
 *     previous speed restores. Save/Load/Settings sub-modals mount
 *     above the menu — the menu stays behind but paused.
 *   - "Return to Main" reloads the page. This is the same pattern used
 *     by MFix2.1 for the Exit path and by the current in-battle "Main
 *     Menu" button — cleanest way to tear down every stateful client
 *     module (sim driver, HUDs, panels, event listeners, atlases).
 *   - "Load from in-game" stashes the picked SavedGame in
 *     sessionStorage under `su:pending-load`, then reloads. main.ts's
 *     bootstrap reads that key first — if present, it boots straight
 *     into the loaded game instead of showing the main menu. Keeps
 *     the load path uniform with a full page reload.
 *
 * This module is UI-only; sim state (getting the current SavedGame,
 * writing to slots, restoring speed) is handled by the caller via
 * callbacks. That keeps the menu reusable and testable without a
 * full driver.
 */

import { injectMenuStyles } from './menu-styles';

export interface InGameMenuCallbacks {
  /** Close the menu and restore the sim speed captured when the menu opened. */
  onResume(): void;
  /** Open the save-slot picker in save mode. Menu should stay mounted underneath. */
  onOpenSavePicker(): void;
  /** Open the save-slot picker in load mode. Menu should stay mounted underneath. */
  onOpenLoadPicker(): void;
  /** Open the settings modal. Menu should stay mounted underneath. */
  onOpenSettings(): void;
  /** Return to Main Menu — caller should confirm + reload the page. */
  onReturnToMain(): void;
  /** Exit the game (same behaviour as main-menu Exit button). */
  onExit(): void;
}

export interface InGameMenuHandle {
  /** Tear down the overlay + remove listeners. Idempotent. */
  destroy(): void;
}

/**
 * Mount the in-game menu. The overlay uses the shared menu classes
 * (z-index 1000) so it sits above HUDs (500), settlement panels (700),
 * and the battle view backdrop (800).
 */
export function mountInGameMenu(
  root: HTMLElement,
  cb: InGameMenuCallbacks,
): InGameMenuHandle {
  injectMenuStyles();

  const overlay = document.createElement('div');
  overlay.className = 'su-menu-overlay';
  root.appendChild(overlay);

  const panel = document.createElement('div');
  panel.className = 'su-menu-panel';
  overlay.appendChild(panel);

  // Escape closes via Resume. Attached on window so it fires regardless
  // of focus. Note: the settings modal + save picker attach their own
  // Escape handlers when mounted; both fire, but the sub-modal's
  // handler (attached later) runs after and closes only the sub-modal.
  // Order of teardown ensures we don't accidentally close the in-game
  // menu while a sub-modal is up.
  const escHandler = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      // If a sub-modal is up (its overlay was appended after this one),
      // it will handle Escape first. This handler only fires if the
      // in-game menu is the top-most modal.
      const overlays = root.querySelectorAll('.su-menu-overlay');
      const topmost = overlays[overlays.length - 1];
      if (topmost !== overlay) return;
      ev.preventDefault();
      cb.onResume();
    }
  };
  window.addEventListener('keydown', escHandler);

  render(panel);

  let destroyed = false;
  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('keydown', escHandler);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    },
  };

  function render(host: HTMLElement): void {
    host.innerHTML = '';

    const title = document.createElement('h1');
    title.className = 'su-menu-title';
    title.textContent = 'Paused';
    host.appendChild(title);

    const sub = document.createElement('div');
    sub.className = 'su-menu-subtitle';
    sub.textContent = 'The sim is paused while this menu is open.';
    host.appendChild(sub);

    host.appendChild(makeButton('Resume', () => cb.onResume(), true));
    host.appendChild(makeButton('Save Game', () => cb.onOpenSavePicker(), false));
    host.appendChild(makeButton('Load Game', () => cb.onOpenLoadPicker(), false));
    host.appendChild(makeButton('Settings', () => cb.onOpenSettings(), false));
    host.appendChild(makeButton('Return to Main Menu', () => {
      if (window.confirm('Return to Main Menu? Any unsaved progress will be lost.')) {
        cb.onReturnToMain();
      }
    }, false));
    host.appendChild(makeButton('Exit', () => {
      if (window.confirm('Exit the game? Any unsaved progress will be lost.')) {
        cb.onExit();
      }
    }, false));
  }
}

function makeButton(label: string, onClick: () => void, primary: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = primary ? 'su-menu-button su-primary' : 'su-menu-button';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}
